import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Package, TrendingUp, Layers, AlertCircle, Zap } from 'lucide-react';
import { LoadingState } from '../ui';
import useAnalyticsStore from '../../stores/analyticsStore';
import { getMaterialOverview, getMaterialTurnover } from '../../services/analyticsService';

const MaterialAnalyticsTab = () => {
  const { dateRange } = useAnalyticsStore();

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['material-overview', dateRange],
    queryFn: () => getMaterialOverview({ days: dateRange }),
    staleTime: 60000
  });

  const { data: turnover, isLoading: turnoverLoading } = useQuery({
    queryKey: ['material-turnover', dateRange],
    queryFn: () => getMaterialTurnover({ days: dateRange }),
    staleTime: 60000
  });

  if (overviewLoading || turnoverLoading) {
    return <LoadingState message="Loading material analytics..." />;
  }

  if (!overview || !overview.materials || overview.materials.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No material data available for the selected period</p>
      </div>
    );
  }

  const abcSummary = overview.abc_summary || { A: 0, B: 0, C: 0 };
  const materials = overview.materials || [];
  const turnoverData = turnover?.turnover_data || [];

  // ABC Pie chart data
  const abcPieData = [
    { name: 'Class A', value: abcSummary.A, color: '#10B981' },
    { name: 'Class B', value: abcSummary.B, color: '#3B82F6' },
    { name: 'Class C', value: abcSummary.C, color: '#F59E0B' }
  ];

  // Top materials by quantity
  const topMaterials = [...materials]
    .sort((a, b) => b.total_quantity - a.total_quantity)
    .slice(0, 10);

  // Velocity data
  const velocityData = turnoverData.slice(0, 10).map(m => ({
    name: m.material_code,
    receipts: m.avg_receipts_per_day,
    category: m.velocity_category
  }));

  const getAbcColor = (abc) => {
    const colors = {
      'A': 'bg-green-100 text-green-800 border-green-300',
      'B': 'bg-blue-100 text-blue-800 border-blue-300',
      'C': 'bg-yellow-100 text-yellow-800 border-yellow-300'
    };
    return colors[abc] || colors['C'];
  };

  const getVelocityColor = (velocity) => {
    const colors = {
      'Fast': '#10B981',
      'Medium': '#3B82F6',
      'Slow': '#F59E0B'
    };
    return colors[velocity] || colors['Slow'];
  };

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPICard
          title="Total Materials"
          value={overview.total_materials || 0}
          icon={Package}
          color="blue"
        />
        <KPICard
          title="Total Receipts"
          value={overview.total_receipts || 0}
          icon={Layers}
          color="green"
        />
        <KPICard
          title="Class A Items"
          value={abcSummary.A || 0}
          icon={TrendingUp}
          color="purple"
          subtitle="80% of value"
        />
        <KPICard
          title="Fast Moving"
          value={turnoverData.filter(m => m.velocity_category === 'Fast').length}
          icon={Zap}
          color="orange"
        />
      </div>

      {/* ABC Analysis & Top Materials */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ABC Pie Chart */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">ABC Classification</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={abcPieData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                dataKey="value"
                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
              >
                {abcPieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 mt-4">
            {abcPieData.map((entry, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className="text-sm text-gray-600">{entry.name}: {entry.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Materials Chart */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 10 Materials by Quantity</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={topMaterials} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="material_code" width={80} fontSize={12} />
              <Tooltip />
              <Bar dataKey="total_quantity" fill="#3B82F6" radius={[0, 8, 8, 0]}>
                {topMaterials.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.abc_class === 'A' ? '#10B981' : entry.abc_class === 'B' ? '#3B82F6' : '#F59E0B'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Material Velocity Chart */}
      {velocityData.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Material Velocity (Avg Receipts/Day)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={velocityData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis label={{ value: 'Receipts/Day', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Bar dataKey="receipts" radius={[8, 8, 0, 0]}>
                {velocityData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getVelocityColor(entry.category)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-6 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#10B981' }} />
              <span className="text-sm text-gray-600">Fast ({'>'} 1/day)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#3B82F6' }} />
              <span className="text-sm text-gray-600">Medium (0.3-1/day)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#F59E0B' }} />
              <span className="text-sm text-gray-600">Slow ({'<'} 0.3/day)</span>
            </div>
          </div>
        </div>
      )}

      {/* Materials Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Material Inventory Analysis</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Material
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ABC Class
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Receipts
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Quantity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Velocity
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {materials.slice(0, 20).map((material, index) => {
                const velocity = turnoverData.find(t => t.material_code === material.material_code);
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{material.material_code}</div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">{material.material_description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getAbcColor(material.abc_class)}`}>
                        {material.abc_class}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {material.receipts}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {material.total_quantity.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {velocity && (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          velocity.velocity_category === 'Fast' ? 'bg-green-100 text-green-800' :
                          velocity.velocity_category === 'Medium' ? 'bg-blue-100 text-blue-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {velocity.velocity_category}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// KPI Card Component
const KPICard = ({ title, value, icon: Icon, color, subtitle }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600'
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
      {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
    </div>
  );
};

export default MaterialAnalyticsTab;
